"use strict";
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Pipeline = void 0;
var lodash_sortby_1 = __importDefault(require("lodash.sortby"));
var Sample_1 = require("../Sample");
var isHtmlCanvasElement_1 = require("../util/isHtmlCanvasElement");
var makeBitmapSubset_1 = require("../util/makeBitmapSubset");
var makeMultiDimensional_1 = require("../util/makeMultiDimensional");
var PipelineState_1 = require("./PipelineState");
/**
 * Top-level API of the canvas rendering pipeline.
 *
 * @see https://dev.azure.com/fotofabriek-cra/Common/_wiki/wikis/Wiki.wiki/155/Tutorial
 */
var Pipeline = /** @class */ (function () {
    /**
     * As of 5.0.0, the second parameter has been changed to {@link Device}
     * instead of `() =>` {@link HTMLCanvasElement}. This was a breaking
     * change. Migration tips:
     *
     * - For browser-based apps, preferably use {@link createDOMPipeline}. Otherwise,
     *   replace your old `createCanvas` argument with: `ff.Device.browser()`
     *
     * - For server-side apps, simply wrap your existing `createCanvas` argument
     *   like so: `new ff.Device(createCanvas)`
     */
    function Pipeline(canvas, device, onResourceChanged, onResourceError, plugins) {
        if (plugins === void 0) { plugins = []; }
        this.canvas = canvas;
        this.device = device;
        this.onResourceChanged = onResourceChanged;
        this.onResourceError = onResourceError;
        this.plugins = plugins;
        this._isPixelRatioApplied = false;
        this._paintersByType = {};
        this._state = new PipelineState_1.PipelineState(canvas, this);
    }
    /**
     * On high-DPI devices, this method will increase the virtual resolution of
     * the main canvas. If the device pixel ratio is not 1, this method MUST be
     * called once before calling {@link paint} or {@link paintAll}.
     *
     * (The canvas' on-screen size should be controlled via CSS, as virtual
     * resolution works by changing the width/height attributes.)
     *
     * @see https://www.html5rocks.com/en/tutorials/canvas/hidpi/
     */
    Pipeline.prototype.applyPixelRatio = function () {
        if (!this._isPixelRatioApplied) {
            var width = this._getElementWidth() * this.device.pixelRatio;
            var height = this._getElementHeight() * this.device.pixelRatio;
            this.canvas.setAttribute("width", width.toString());
            this.canvas.setAttribute("height", height.toString());
            this._state.ctx.scale(this.device.pixelRatio, this.device.pixelRatio);
            this.confirmPixelRatioApplied();
        }
    };
    Pipeline.prototype._getElementWidth = function () {
        if (isHtmlCanvasElement_1.isHtmlCanvasElement(this.canvas)) {
            return this.canvas.width;
        }
        else {
            return parseFloat(this.canvas.getAttribute("width") || "0");
        }
    };
    Pipeline.prototype._getElementHeight = function () {
        if (isHtmlCanvasElement_1.isHtmlCanvasElement(this.canvas)) {
            return this.canvas.height;
        }
        else {
            return parseFloat(this.canvas.getAttribute("height") || "0");
        }
    };
    /**
     * Confirms that the main canvas is configured for high-DPI rendering. This
     * may be called instead of {@link applyPixelRatio} if the main canvas was
     * already configured as such.
     *
     * @see https://www.html5rocks.com/en/tutorials/canvas/hidpi/
     */
    Pipeline.prototype.confirmPixelRatioApplied = function () {
        this._isPixelRatioApplied = true;
    };
    /**
     * Gets the canvas width before virtual resolution.
     */
    Pipeline.prototype._getWidth = function () {
        return this._getElementWidth() / this.device.pixelRatio;
    };
    /**
     * Gets the canvas height before virtual resolution.
     */
    Pipeline.prototype._getHeight = function () {
        return this._getElementHeight() / this.device.pixelRatio;
    };
    /**
     * Sets up a painter to be used for an object type.
     *
     * @param painter The painter to set up.
     */
    Pipeline.prototype.use = function (painter) {
        this._paintersByType[painter.type] = painter;
        return this;
    };
    /**
     * Call this to asynchronously preload external resources, such as images. This method is
     * intended for server-side rendering. Example usage:
     *
     * ```
     * pipeline.preloadResources(objects)
     *     .then(() => pipeline.paintAll(objects))
     *     .then(() => console.log(canvas.toDataUrl()))
     *     .catch(e => console.error(e));
     * ```
     *
     * @param objects The objects whose resources to preload.
     */
    Pipeline.prototype.preloadResources = function (objects) {
        var e_1, _a;
        var _this = this;
        var pluginPromises = this.plugins.map(function (plugin) { return plugin.preload(objects, _this._state); });
        try {
            for (var objects_1 = __values(objects), objects_1_1 = objects_1.next(); !objects_1_1.done; objects_1_1 = objects_1.next()) {
                var object = objects_1_1.value;
                this._loadResources(object);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (objects_1_1 && !objects_1_1.done && (_a = objects_1.return)) _a.call(objects_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return Promise.all(__spread([this._state.promiseAll()], pluginPromises));
    };
    /**
     * Paints an array of objects on the canvas, from first to last.
     *
     * @param objects The objects to paint.
     */
    Pipeline.prototype.paintAll = function (objects) {
        var e_2, _a, e_3, _b, e_4, _c;
        try {
            for (var _d = __values(this.plugins), _e = _d.next(); !_e.done; _e = _d.next()) {
                var plugin = _e.value;
                plugin.onBeforePaintAll(this._state, objects);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_e && !_e.done && (_a = _d.return)) _a.call(_d);
            }
            finally { if (e_2) throw e_2.error; }
        }
        try {
            for (var _f = __values(lodash_sortby_1.default(objects, [function (o) { return o.zIndex || 0; }])), _g = _f.next(); !_g.done; _g = _f.next()) {
                var object = _g.value;
                this.paint(object);
            }
        }
        catch (e_3_1) { e_3 = { error: e_3_1 }; }
        finally {
            try {
                if (_g && !_g.done && (_b = _f.return)) _b.call(_f);
            }
            finally { if (e_3) throw e_3.error; }
        }
        try {
            for (var _h = __values(this.plugins), _j = _h.next(); !_j.done; _j = _h.next()) {
                var plugin = _j.value;
                plugin.onAfterPaintAll(this._state, objects);
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (_j && !_j.done && (_c = _h.return)) _c.call(_h);
            }
            finally { if (e_4) throw e_4.error; }
        }
    };
    /**
     * Paints a single object on the canvas.
     *
     * @param object The object to paint.
     */
    Pipeline.prototype.paint = function (object) {
        var e_5, _a, e_6, _b;
        if (this.device.pixelRatio !== 1 && !this._isPixelRatioApplied) {
            throw new Error("Call `pipeline.applyPixelRatio()` or "
                + "`pipeline.confirmPixelRatioApplied()` once first.");
        }
        if (this.isVisible(object)) {
            try {
                for (var _c = __values(this.plugins), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var plugin = _d.value;
                    plugin.onBeforePaint(this._state, object);
                }
            }
            catch (e_5_1) { e_5 = { error: e_5_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                }
                finally { if (e_5) throw e_5.error; }
            }
            this._loadResources(object);
            this._state.ctx.save();
            this.device.scale(this._state.ctx);
            this.device.translate(this._state.ctx);
            this._translateToCenterOrigin(this._state.ctx);
            this.device.rotate(this._state.ctx);
            this._requirePainterFor(object).paint(object, this._state);
            this._state.ctx.restore();
            this._state.invalidateCachedBitmap();
            try {
                for (var _e = __values(this.plugins), _f = _e.next(); !_f.done; _f = _e.next()) {
                    var plugin = _f.value;
                    plugin.onAfterPaint(this._state, object);
                }
            }
            catch (e_6_1) { e_6 = { error: e_6_1 }; }
            finally {
                try {
                    if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
                }
                finally { if (e_6) throw e_6.error; }
            }
        }
    };
    /**
     * Indicates whether an object will be visible on the canvas. An object is invisible if
     * its width/height rounds down to 0. (Note: does not yet check if the object falls
     * within the boundaries of the canvas, but might do so in the future.)
     *
     * @param object The object to measure.
     */
    Pipeline.prototype.isVisible = function (object) {
        return this.paintWidthOf(object) > 0 && this.paintHeightOf(object) > 0;
    };
    /**
     * Makes [x=0, y=0] point at the center of the canvas.
     */
    Pipeline.prototype._translateToCenterOrigin = function (ctx) {
        ctx.translate(Math.ceil(0.5 * this._getWidth()), Math.ceil(0.5 * this._getHeight()));
    };
    /**
     * Clears the canvas.
     *
     * @param backgroundColor The color to use, e.g. "white". Defaults to transparent.
     */
    Pipeline.prototype.clear = function (backgroundColor) {
        var ctx = this._state.ctx;
        var width = this._getWidth();
        var height = this._getHeight();
        ctx.clearRect(0, 0, width, height);
        if (backgroundColor) {
            ctx.save();
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, width, height);
            ctx.restore();
        }
        return this;
    };
    /**
     * Returns the total size required to paint an object (at a 0-angle, i.e. upright).
     *
     * @param object The object to measure.
     */
    Pipeline.prototype.paintSizeOf = function (object) {
        return this._requirePainterFor(object).paintSizeOf(object);
    };
    /**
     * Returns the total width required to paint an object (at a 0-angle, i.e. upright).
     *
     * @param object The object to measure.
     */
    Pipeline.prototype.paintWidthOf = function (object) {
        return this._requirePainterFor(object).paintWidthOf(object);
    };
    /**
     * Returns the total height required to paint an object (at a 0-angle, i.e. upright).
     *
     * @param object The object to measure.
     */
    Pipeline.prototype.paintHeightOf = function (object) {
        return this._requirePainterFor(object).paintHeightOf(object);
    };
    /**
     * Returns the object boundaries for GUI purposes (e.g. for resize handles).
     *
     * @param object The object to measure.
     */
    Pipeline.prototype.boundsOf = function (object) {
        return this._requirePainterFor(object).boundsOf(object);
    };
    /**
     * Gets the resource store of an object.
     *
     * @param object The object whose resource store to get.
     */
    Pipeline.prototype.storeOf = function (object) {
        return this._state.storeOf(object);
    };
    /**
     * Returns the painter that will be used for the given object. Throws if no painter
     * matching the object's type has been registered.
     */
    Pipeline.prototype._requirePainterFor = function (object) {
        var painter = this._paintersByType[object.type];
        if (!painter) {
            throw new Error("No painter registered for object type: \"" + object.type + "\".");
        }
        return painter;
    };
    /**
     * Delegates to {@link Painter.loadResources}.
     */
    Pipeline.prototype._loadResources = function (object) {
        var store = this._state.storeOf(object);
        this._requirePainterFor(object).loadResources(store, object);
    };
    /**
     * Gets the object IDs for which resources are currently being held.
     */
    Pipeline.prototype.getActiveObjectIds = function () {
        return this._state.getActiveObjectIds();
    };
    /**
     * Disposes all resources held for objects.
     */
    Pipeline.prototype.disposeAll = function () {
        this._state.disposeAll();
    };
    /**
     * Disposes all resources held for an object.
     *
     * @param object The object being disposed.
     */
    Pipeline.prototype.dispose = function (object) {
        this._state.dispose(object);
    };
    /**
     * Disposes all resources held for an object.
     *
     * @param id The ID of the object being disposed.
     */
    Pipeline.prototype.disposeById = function (id) {
        this._state.disposeById(id);
    };
    /**
     * Designed for pipette functionality. Samples the pixel color at the given
     * coordinate, as well as the surrounding pixels (which the application may
     * use to show a magnified preview).
     *
     * Note: this samples the current contents of `this.canvas`, meaning the last
     * rendered frame including any changes made externally.
     *
     * @param x The X-coordinate of the center point.
     * @param y The Y-coordinate of the center point.
     * @param size The width and height of the area to sample.
     */
    Pipeline.prototype.sample = function (x, y, size) {
        if (!Number.isInteger(x) || !Number.isInteger(y)) {
            throw new Error("X/Y supplied are not integers, round or floor X/Y before using.");
        }
        var bitmap = this._state.getOrCreateBitmap();
        var halfSize = Math.floor(size / 2);
        var pixels = makeBitmapSubset_1.makeBitmapSubset(bitmap, x - halfSize, y - halfSize, size, size).data;
        var area = makeMultiDimensional_1.makeMultiDimensional(pixels, size);
        var target = area[halfSize][halfSize];
        return new Sample_1.Sample(target, area);
    };
    return Pipeline;
}());
exports.Pipeline = Pipeline;
//# sourceMappingURL=Pipeline.js.map